/**
 * Utility functions for processing and handling chat messages
 */

// Removed stream status config imports - using string literals
import { useThreadStore } from "@/store";

// Add this improved function towards the top of the file (after imports and constants)
export const ensureMessageSavedToDatabase = async (
  messageData,
  threadStore
) => {
  try {
    if (!messageData || !threadStore) return false;

    // Special handling for image generation messages - mark them as critical
    const hasImageGeneration =
      (messageData.tool_calls &&
        JSON.stringify(messageData.tool_calls).includes("image_gen")) ||
      (messageData.content && messageData.content.includes("![")) ||
      (messageData.metadata &&
        messageData.metadata.toolName &&
        (messageData.metadata.toolName.includes("image") ||
          messageData.metadata.toolName.includes("dalle") ||
          messageData.metadata.toolName.includes("image_gen")));

    if (hasImageGeneration) {
      // Add high priority flag for image messages
      messageData.metadata = {
        ...(messageData.metadata || {}),
        save_priority: "critical",
        has_image_generation: true,
        image_message: true,
        needs_persistence: true,
        // Keep a lightweight version of any image data
        image_metadata: {
          timestamp: new Date().toISOString(),
          hasImage: true,
        },
      };

      // Force immediate save attempt for image messages
      try {
        const { supabase } = await import("@/lib/supabase");
        // Clean the message for database storage, removing any large base64 data
        const dbSafeMsg = { ...messageData };

        // Get workspace_id from threadStore if not in messageData
        const threadState = threadStore?.getState?.() || {};
        const threadId = messageData.thread_id || messageData.threadId;

        // Try multiple sources for workspace_id
        let workspaceId =
          messageData.workspace_id ||
          messageData.workspaceId ||
          threadState.currentThread?.workspace_id ||
          null;

        // If still not found, try to find it from the threads array
        if (!workspaceId && threadId && threadState.threads) {
          const thread = threadState.threads.find((t) => t.id === threadId);
          if (thread?.workspace_id) {
            workspaceId = thread.workspace_id;
          }
        }

        // Last resort: try to get from authStore's activeWorkspace
        if (!workspaceId) {
          try {
            const { useAuthStore } = await import("@/store/authStore");
            const authState = useAuthStore.getState();
            workspaceId =
              authState.activeWorkspace?.id ||
              authState.currentWorkspace?.id ||
              null;
          } catch (e) {
            // Ignore import errors
          }
        }

        // Final fallback: query database for thread's workspace_id
        if (!workspaceId && threadId) {
          try {
            const { data: threadData } = await supabase
              .from("threads")
              .select("workspace_id")
              .eq("id", threadId)
              .single();

            if (threadData?.workspace_id) {
              workspaceId = threadData.workspace_id;
            }
          } catch (dbError) {
            // Ignore database query errors - we'll log the warning below
          }
        }

        if (!workspaceId) {
          console.error(
            "Warning: workspace_id is missing for image message, cannot save:",
            {
              messageId: messageData.id,
              threadId: threadId,
              currentThread: threadState.currentThread,
              hasThreads: !!threadState.threads,
              threadsCount: threadState.threads?.length || 0,
            }
          );
        }

        // Ensure critical fields are set
        dbSafeMsg.id = messageData.id;
        dbSafeMsg.message_id = messageData.id;
        dbSafeMsg.thread_id = messageData.thread_id || messageData.threadId;
        dbSafeMsg.workspace_id = workspaceId;
        dbSafeMsg.content = messageData.content || " ";
        // CRITICAL FIX: Preserve the original role, don't default to "assistant"
        dbSafeMsg.role = messageData.role;

        // Only add default role if absolutely no role is provided (should be rare)
        if (!dbSafeMsg.role) {
          console.error(
            "Message missing role! This should not happen:",
            messageData
          );
          dbSafeMsg.role = "assistant"; // Emergency fallback only
        }

        // Skip save if workspace_id is still missing
        if (!dbSafeMsg.workspace_id) {
          console.error(
            "Cannot save image message: workspace_id is required but not available"
          );
          // Continue with normal save process as fallback
          return false;
        }

        // Remove UI-specific fields
        delete dbSafeMsg.isStreaming;
        delete dbSafeMsg.isStreamComplete;
        delete dbSafeMsg.messageStatus;
        delete dbSafeMsg.skipDatabaseSave;
        delete dbSafeMsg.message_index; // UI-only field, not in database schema

        delete dbSafeMsg.threadId;
        delete dbSafeMsg.workspaceId;

        // Try immediate database save
        const { error } = await supabase
          .from("thread_messages")
          .upsert([dbSafeMsg], { onConflict: "id" });

        if (error) {
          console.error("Error in immediate image message save:", error);
        } else {
          // Store a record that this message was saved
          try {
            localStorage.setItem(
              `image_message_saved_${messageData.id}`,
              JSON.stringify({
                timestamp: Date.now(),
                thread_id: dbSafeMsg.thread_id,
              })
            );
          } catch (e) {
            /* Ignore localStorage errors */
          }

          return true; // Early return if immediate save succeeded
        }
      } catch (immediateError) {
        console.error(
          "Error in immediate image message handling:",
          immediateError
        );
        // Continue with normal save process as fallback
      }
    }

    // Make sure both id and message_id are set and identical
    const messageId = messageData.id;

    // Detect if this is a follow-up message (content message) or tool call message
    const isFollowUpMessage =
      messageData.role === "assistant" &&
      (messageData.metadata?.isFollowUpContent === true ||
        messageData.metadata?.followUpCompleted === true ||
        messageData.metadata?.hasFollowUp === true ||
        messageData.metadata?.hasContentInFollowUp === true);

    const isToolCallMessage =
      messageData.role === "assistant" &&
      (messageData.metadata?.isToolCallMessage === true ||
        messageData.metadata?.hasContentInToolCall === true ||
        (messageData.tool_calls && messageData.tool_calls.length > 0));

    // For tool call messages, verify corresponding follow-up hasn't been saved yet
    if (isToolCallMessage) {
      try {
        // Check if there's already a follow-up content message saved for this tool call
        const { supabase } = await import("@/lib/supabase");
        const { data: existingFollowUp } = await supabase
          .from("thread_messages")
          .select("id, created_at")
          .eq("metadata->>toolCallMessageId", messageId)
          .limit(1);

        if (existingFollowUp && existingFollowUp.length > 0) {
          // If follow-up exists, add a priority flag to ensure this tool call message is saved with higher priority
          messageData.metadata = {
            ...(messageData.metadata || {}),
            save_priority: "critical",
            save_sequence: 0, // Ensure it's saved before anything else
            isCriticalFix: true,
            fixTimestamp: Date.now(),
          };
        }
      } catch (checkError) {
        console.error("Error checking for existing follow-up:", checkError);
      }
    }

    // For follow-up messages, ensure the tool call message is saved first
    if (isFollowUpMessage && messageData.metadata?.toolCallMessageId) {
      try {
        const toolCallId = messageData.metadata.toolCallMessageId;

        // Check if tool call message exists in database
        const { supabase } = await import("@/lib/supabase");
        const { data: existingToolCall } = await supabase
          .from("thread_messages")
          .select("id")
          .eq("id", toolCallId)
          .single();

        if (!existingToolCall) {
          // If tool call doesn't exist yet, add a delay and then verify again
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Check again
          const { data: retryToolCall } = await supabase
            .from("thread_messages")
            .select("id")
            .eq("id", toolCallId)
            .single();

          if (!retryToolCall) {
            console.warn(
              `❌ Tool call message ${toolCallId} still missing after delay. Will attempt to recover.`
            );

            // Try to find the tool call message in the thread store
            const threadState = threadStore.getState?.() || {};
            const messages = threadState.messages || [];
            const toolCallMsg = messages.find((msg) => msg.id === toolCallId);

            if (toolCallMsg) {
              // Create a minimal version of the tool call message and save it
              const emergencyToolCallMsg = {
                id: toolCallId,
                message_id: toolCallId,
                thread_id: messageData.thread_id,
                workspace_id: messageData.workspace_id,
                role: "assistant",
                content: toolCallMsg.content || " ",
                created_at: toolCallMsg.created_at || new Date().toISOString(),
                updated_at: new Date().toISOString(),
                provider:
                  toolCallMsg.provider || messageData.provider || "openai",
                tool_calls:
                  typeof toolCallMsg.tool_calls === "string"
                    ? toolCallMsg.tool_calls
                    : JSON.stringify(toolCallMsg.tool_calls || []),
                tools_count:
                  toolCallMsg.tools_count ||
                  toolCallMsg.tool_calls?.length ||
                  0,
                metadata: {
                  isToolCallMessage: true,
                  hasContentInToolCall: true,
                  isEmergencySave: true,
                  timestamp: new Date().toISOString(),
                },
              };

              // Attempt emergency save of tool call message
              try {
                const { error: emergencySaveError } = await supabase
                  .from("thread_messages")
                  .upsert([emergencyToolCallMsg], {
                    onConflict: "id",
                    ignoreDuplicates: false,
                  });

                if (emergencySaveError) {
                  console.error(
                    "Emergency tool call message save failed:",
                    emergencySaveError
                  );
                } else {
                  await new Promise((resolve) => setTimeout(resolve, 500));
                }
              } catch (emergencySaveError) {
                console.error(
                  "Exception during emergency tool call save:",
                  emergencySaveError
                );
              }
            }
          }
        }
      } catch (checkError) {
        console.error("Error checking for tool call message:", checkError);
      }
    }

    // Get workspace_id from threadStore if not in messageData
    const threadState =
      typeof threadStore?.getState === "function" ? threadStore.getState() : {};
    const threadId = messageData.thread_id || messageData.threadId;

    // Try multiple sources for workspace_id
    let workspaceId =
      messageData.workspace_id ||
      messageData.workspaceId ||
      threadState.currentThread?.workspace_id ||
      null;

    // If still not found, try to find it from the threads array
    if (!workspaceId && threadId && threadState.threads) {
      const thread = threadState.threads.find((t) => t.id === threadId);
      if (thread?.workspace_id) {
        workspaceId = thread.workspace_id;
      }
    }

    // Last resort: try to get from authStore's activeWorkspace
    if (!workspaceId) {
      try {
        const { useAuthStore } = await import("@/store/authStore");
        const authState = useAuthStore.getState();
        workspaceId =
          authState.activeWorkspace?.id ||
          authState.currentWorkspace?.id ||
          null;
      } catch (e) {
        // Ignore import errors
      }
    }

    // Final fallback: query database for thread's workspace_id
    if (!workspaceId && threadId) {
      try {
        const { supabase } = await import("@/lib/supabase");
        const { data: threadData } = await supabase
          .from("threads")
          .select("workspace_id")
          .eq("id", threadId)
          .single();

        if (threadData?.workspace_id) {
          workspaceId = threadData.workspace_id;
        }
      } catch (dbError) {
        // Ignore database query errors - we'll log the warning below
      }
    }

    if (!workspaceId) {
      console.error(
        "Warning: workspace_id is missing for message, cannot save:",
        {
          messageId,
          threadId: threadId,
          currentThread: threadState.currentThread,
          hasThreads: !!threadState.threads,
          threadsCount: threadState.threads?.length || 0,
        }
      );
      // Return early if workspace_id is required but not available
      // Note: Some messages might not have workspace_id in legacy data, but new messages should always have it
      // We'll attempt to save anyway and let the database constraint catch it if needed
    }

    // Sanitize message data to ensure it only contains fields that exist in the database
    const sanitizedMessageData = {
      id: messageId,
      message_id: messageId, // Ensure message_id is ALWAYS set
      content: messageData.content || " ",
      role: messageData.role,
      thread_id: messageData.thread_id || messageData.threadId,
      workspace_id: workspaceId,
      created_at: messageData.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      provider: messageData.provider || "openai",
      provider_message_id: messageData.provider_message_id || messageId,
    };

    // Explicitly remove UI-specific fields that don't exist in the database schema
    delete sanitizedMessageData.isStreaming;
    delete sanitizedMessageData.isStreamComplete;
    delete sanitizedMessageData.messageStatus;

    delete sanitizedMessageData.skipDatabaseSave;
    delete sanitizedMessageData.skipStateUpdate; // CRITICAL: Remove this flag before DB save
    delete sanitizedMessageData.threadId;
    delete sanitizedMessageData.workspaceId;
    delete sanitizedMessageData.messageId;

    // For follow-up messages (content messages), use appropriate metadata and explicitly remove tool_calls
    if (isFollowUpMessage) {
      // Check for duplicate save prevention flag
      if (messageData.metadata?.preventDuplicateSave === true) {
        // Check if there's already a saved follow-up message for this tool call
        if (messageData.metadata?.toolCallMessageId) {
          try {
            const { supabase } = await import("@/lib/supabase");
            const { data: existingFollowUp } = await supabase
              .from("thread_messages")
              .select("id, created_at")
              .eq(
                "metadata->>toolCallMessageId",
                messageData.metadata.toolCallMessageId
              )
              .eq("metadata->>isFollowUpContent", "true")
              .limit(1);

            if (existingFollowUp && existingFollowUp.length > 0) {
              return true; // Return success without saving
            }
          } catch (checkError) {
            console.error("Error checking for existing follow-up:", checkError);
          }
        }
      }

      sanitizedMessageData.metadata = {
        timestamp: new Date().toISOString(),
        isFollowUpContent: true,
        hasContentInFollowUp: true,
        followUpCompleted: true,
        save_sequence: 2, // Always use sequence 2 for follow-up messages
        // CRITICAL: Preserve provider information from original metadata
        provider:
          messageData.metadata?.provider || messageData.provider || "openai",
        model: messageData.metadata?.model || messageData.model,
        ...(messageData.metadata?.toolCallMessageId
          ? { toolCallMessageId: messageData.metadata.toolCallMessageId }
          : {}),
        // CRITICAL: Preserve memory extraction metadata
        ...(messageData.metadata?.extractedMemories
          ? { extractedMemories: messageData.metadata.extractedMemories }
          : {}),
        ...(messageData.metadata?.memoryExtractionTime
          ? { memoryExtractionTime: messageData.metadata.memoryExtractionTime }
          : {}),
      };
      sanitizedMessageData.tool_calls = null;
      sanitizedMessageData.tools_count = 0;
    }
    // For tool call messages, ensure tool_calls is properly set
    else if (isToolCallMessage) {
      try {
        // If it's already a string, keep it that way
        if (typeof messageData.tool_calls === "string") {
          sanitizedMessageData.tool_calls = messageData.tool_calls;
        }
        // If it's an array, stringify it
        else if (Array.isArray(messageData.tool_calls)) {
          sanitizedMessageData.tool_calls = JSON.stringify(
            messageData.tool_calls
          );
        }
        // Set tools_count based on tool_calls
        sanitizedMessageData.tools_count = Array.isArray(messageData.tool_calls)
          ? messageData.tool_calls.length
          : messageData.tools_count || 0;

        // For tool messages, use appropriate metadata
        sanitizedMessageData.metadata = {
          timestamp: new Date().toISOString(),
          hasTool: true,
          isToolCallMessage: true,
          hasContentInToolCall: true,
          save_sequence: 1, // Always use sequence 1 for tool call messages
          save_priority: "high",
          // CRITICAL: Preserve provider information from original metadata
          provider:
            messageData.metadata?.provider || messageData.provider || "openai",
          model: messageData.metadata?.model || messageData.model,
          // CRITICAL: Preserve memory extraction metadata
          ...(messageData.metadata?.extractedMemories
            ? { extractedMemories: messageData.metadata.extractedMemories }
            : {}),
          ...(messageData.metadata?.memoryExtractionTime
            ? { memoryExtractionTime: messageData.metadata.memoryExtractionTime }
            : {}),
        };
      } catch (toolError) {
        console.error("Error processing tool_calls for database:", toolError);
      }
    }
    // For regular messages with metadata
    else if (messageData.metadata && !isFollowUpMessage) {
      // Create a minimal metadata object with only essential fields
      sanitizedMessageData.metadata = {
        timestamp: messageData.metadata.timestamp || new Date().toISOString(),
        // CRITICAL: Preserve provider information from original metadata
        provider:
          messageData.metadata?.provider || messageData.provider || "openai",
        model: messageData.metadata?.model || messageData.model,
        // CRITICAL: Preserve memory extraction metadata
        ...(messageData.metadata?.extractedMemories
          ? { extractedMemories: messageData.metadata.extractedMemories }
          : {}),
        ...(messageData.metadata?.memoryExtractionTime
          ? { memoryExtractionTime: messageData.metadata.memoryExtractionTime }
          : {}),
      };
    }
    // For all other messages, use empty metadata
    else {
      sanitizedMessageData.metadata = {};
    }

    // CRITICAL: Fetch current metadata from database and merge to preserve any
    // asynchronously-added fields (like extractedMemories from memory extraction)
    try {
      const { supabase } = await import("@/lib/supabase");
      const { data: existingMsg } = await supabase
        .from("thread_messages")
        .select("metadata")
        .eq("id", sanitizedMessageData.id)
        .single();

      if (existingMsg?.metadata) {
        // Merge: existing DB metadata as base, then overlay our new metadata
        // This preserves fields like extractedMemories that may have been added async
        sanitizedMessageData.metadata = {
          ...existingMsg.metadata,
          ...sanitizedMessageData.metadata,
        };
      }
    } catch (fetchError) {
      // Message may not exist yet (first save), that's fine
      // Just continue with the metadata we have
    }

    // Add retry logic for database saves
    let saveAttempts = 0;
    const maxSaveAttempts = 3;
    let saveResult = null;

    while (saveAttempts < maxSaveAttempts && !saveResult) {
      saveAttempts++;

      // Try to save using the store's function
      try {
        // Add delay between attempts with exponential backoff
        if (saveAttempts > 1) {
          const backoffMs = Math.min(300 * Math.pow(2, saveAttempts - 1), 2000);
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }

        saveResult = await threadStore
          .getState()
          .saveMessageToDatabase(sanitizedMessageData);

        if (saveResult) {
          return true;
        }
      } catch (saveError) {
        console.error(
          `Error in primary save method (attempt ${saveAttempts}):`,
          saveError
        );

        // On last attempt, try direct database access
        if (saveAttempts === maxSaveAttempts) {
          // Fallback to direct database access using Supabase
          try {
            const { supabase } = await import("@/lib/supabase");

            // Try an upsert operation which handles both insert and update
            // IMPORTANT: Use array format for upsert to match successful saves
            const { data, error } = await supabase
              .from("thread_messages")
              .upsert([sanitizedMessageData], {
                // Use array format here
                onConflict: "id",
                ignoreDuplicates: false,
              });

            if (error) {
              console.error("Error in direct upsert:", error);

              // Final fallback - try a simple content-only update
              const { error: updateError } = await supabase
                .from("thread_messages")
                .update({
                  content: sanitizedMessageData.content,
                  updated_at: sanitizedMessageData.updated_at,
                })
                .eq("id", sanitizedMessageData.id);

              if (updateError) {
                throw updateError;
              }

              return true;
            }

            return true;
          } catch (dbError) {
            console.error("All save attempts failed:", dbError);
            return false;
          }
        }
      }
    }

    return !!saveResult;
  } catch (error) {
    console.error("Error in ensureMessageSavedToDatabase:", error);
    return false;
  }
};

// Add this function after ensureMessageSavedToDatabase
export const ensureCriticalMessagesPreserved = async (
  threadId,
  threadStore
) => {
  try {
    if (!threadId || !threadStore) return;

    // Get current thread state
    const threadState =
      typeof threadStore.getState === "function"
        ? threadStore.getState()
        : threadStore;

    // Get current messages
    const currentMessages = threadState.messages || [];

    if (!currentMessages.length) {
      return;
    }

    // Focus on assistant messages that need protection
    const criticalMessages = currentMessages.filter(
      (msg) =>
        msg.role === "assistant" &&
        (msg.tool_calls?.length > 0 || msg.metadata?.followUpCompleted)
    );

    if (criticalMessages.length === 0) {
      return;
    }

    // For each critical message, ensure it's saved to the database
    for (const msg of criticalMessages) {
      // Skip if message is still streaming
      if (msg.isStreaming || msg.messageStatus === "streaming") {
        continue;
      }

      // Create a stable database-ready copy
      const dbMessage = {
        ...msg,
        id: msg.id,
        message_id: msg.id,
        thread_id: threadId,
        updated_at: new Date().toISOString(),
        content: msg.content || " ", // Ensure content is never empty
        skipDatabaseSave: false,
      };

      // Try to save to database
      try {
        await ensureMessageSavedToDatabase(dbMessage, threadStore);
      } catch (saveError) {
        console.error(`Failed to save critical message ${msg.id}:`, saveError);
      }

      // Also ensure message is in thread store
      const updatedMessages = threadState.messages || [];
      const messageExists = updatedMessages.some((m) => m.id === msg.id);

      if (!messageExists) {
        // Add message back to thread store
        threadStore.setState((state) => ({
          ...state,
          messages: [...(state.messages || []), dbMessage],
          preventNewMessageClear: true,
        }));
      }
    }
  } catch (error) {
    console.error("Error in ensureCriticalMessagesPreserved:", error);
  }
};

// DEPRECATED: Use thread store title generation methods instead
export const ensureTitleGeneration = async (threadId, content) => {
  console.warn(
    "ensureTitleGeneration is deprecated. Use thread store enqueueTitleGeneration or generateTitleImmediately instead."
  );

  if (!threadId || !content) {
    console.warn("Missing threadId or content for title generation");
    return;
  }

  try {
    // Use the new thread store method
    const { useThreadStore } = await import("@/store/threadStore");
    const { enqueueTitleGeneration } = useThreadStore.getState();
    enqueueTitleGeneration(threadId, content);
  } catch (error) {
    console.error("Error in deprecated ensureTitleGeneration:", error);
  }
};

// New simplified title generation helper that uses thread store
export const triggerTitleGeneration = async (
  threadId,
  content,
  immediate = false
) => {
  if (!threadId || !content) {
    console.warn("Missing threadId or content for title generation");
    return;
  }

  try {
    const { useThreadStore } = await import("@/store/threadStore");
    const threadStore = useThreadStore.getState();

    if (immediate) {
      // For immediate generation (e.g., first message in thread)
      await threadStore.generateTitleImmediately(threadId, content);
    } else {
      // For queued generation (background processing)
      threadStore.enqueueTitleGeneration(threadId, content);
    }
  } catch (error) {
    console.error("Error triggering title generation:", error);
  }
};

// DEPRECATED: Use thread store ensureThreadVectorStore instead
export const ensureVectorStoreForThread = async (threadId) => {
  console.warn(
    "ensureVectorStoreForThread is deprecated. Use thread store ensureThreadVectorStore instead."
  );

  try {
    const { useThreadStore } = await import("@/store/threadStore");
    const threadStore = useThreadStore.getState();
    return await threadStore.ensureThreadVectorStore(threadId);
  } catch (error) {
    console.error("Error in deprecated ensureVectorStoreForThread:", error);
    return null;
  }
};
/**
 * Extract file citations from a message's content
 *
 * @param {Object} message - The message object to process
 * @returns {Object} The processed message with extracted citations
 */
export const processMessageFileCitations = async (message) => {
  if (!message?.content || typeof message.content !== "string") {
    return message;
  }

  try {
    // Extract annotations if available
    const fileAnnotations =
      message.annotations?.filter(
        (ann) => ann.type === "file_citation" || ann.type === "file_path"
      ) || [];

    // If we don't have annotations but the message has content, try to extract them
    if (fileAnnotations.length === 0 && message.content) {
      // Check for citation patterns in content
      const hasFileCitations = /\[\[(\d+)]]|\[(\d+)](?!\()/.test(
        message.content
      );

      if (hasFileCitations) {
        // Note: File citation processing has been simplified since fullMessageParser was removed
        // This functionality can be re-added later if needed with a simpler approach
      }
    }

    // Return original message if no processing was needed
    return message;
  } catch (error) {
    console.error("Error processing file citations:", error);
    return message;
  }
};

/**
 * An enhanced message update function that processes citations
 *
 * @param {Function} updateMessageState - Original update function from thread store
 * @returns {Function} Enhanced function that processes citations
 */
export const createEnhancedMessageUpdater = (updateMessageState) => {
  return async (messageUpdate) => {
    try {
      // First, apply the basic update with error protection
      try {
        updateMessageState(messageUpdate);
      } catch (updateError) {
        console.error("Error in basic message update:", updateError);
        // Try direct state update as fallback
        useThreadStore.setState((state) => {
          // Find the message in the current messages array
          const messages = state.messages || [];
          const messageIndex = messages.findIndex(
            (m) => m.id === messageUpdate.id
          );

          if (messageIndex >= 0) {
            // Update existing message with CRITICAL role and provider protection
            const existingMessage = messages[messageIndex];
            const updatedMessages = [...messages];

            // CRITICAL FIX: Protect user message properties from corruption
            const protectedUpdate = { ...messageUpdate };

            // Never change role from "user" to anything else unless explicitly intended
            if (
              existingMessage.role === "user" &&
              messageUpdate.role !== "user"
            ) {
              console.warn(
                `🚨 PREVENTED role corruption: Attempted to change user message ${messageUpdate.id} role from "user" to "${messageUpdate.role}"`
              );
              protectedUpdate.role = "user";
            }

            // Preserve user provider
            if (existingMessage.role === "user") {
              protectedUpdate.provider = "user";
            }

            // Preserve original role if not explicitly provided in update
            if (messageUpdate.role === undefined) {
              protectedUpdate.role = existingMessage.role;
            }

            updatedMessages[messageIndex] = {
              ...existingMessage,
              ...protectedUpdate,
            };
            return { messages: updatedMessages };
          } else if (messageUpdate.id) {
            // Add new message - ensure proper role is set
            const newMessage = {
              ...messageUpdate,
              // Default to assistant role for new messages unless explicitly set
              role: messageUpdate.role || "assistant",
            };
            return { messages: [...messages, newMessage] };
          }

          return state; // No changes
        });
      }

      // If this is a complete message (not a streaming update), process file citations
      if (
        messageUpdate.isStreamComplete ||
        messageUpdate.messageStatus === "completed"
      ) {
        try {
          // Get the complete message from state
          const currentState = useThreadStore.getState();
          const completeMessage = currentState.messages.find(
            (m) => m.id === messageUpdate.id
          );

          if (completeMessage) {
            await processMessageFileCitations(completeMessage);
          }
        } catch (error) {
          console.error("Error in enhanced message update:", error);
        }
      }
    } catch (outerError) {
      console.error(
        "Critical error in enhancedUpdateMessageState:",
        outerError
      );
    }
  };
};
